import { Injectable, inject } from '@angular/core';

import * as Sentry from '@sentry/angular';

import { UniversalStorageService } from '@personal-area/storage';

import { UsabillaEventActions } from '../models/usabilla.interface';
import { UsabillaService } from '../usabilla.service';
import { CollectUserDataService } from '../utils/collect-user-data.util';
import { BaseGetFeedbackEvent } from './base-event.interface';

@Injectable({ providedIn: 'root' })
export class SurveyEvent implements BaseGetFeedbackEvent {
  private readonly _usabilla = inject(UsabillaService);
  private readonly _userDataService = inject(CollectUserDataService);
  private readonly _storage = inject(UniversalStorageService);

  private readonly _config = {
    cookieName: 'campaign_open_last_week',
    analyticsUserIds: [32743129, 30011727, 31427569, 33018200, 20957465, 31008717],
    expirationDays: {
      analytics: -1, // Expire cookie immediately to allow analytics to always see the survey
      users: 3,
    },
    maxClosureCount: 3,
  };

  public init(): void {
    this._usabilla.executeEvent(
      'setEventCallback',
      (_: string, action: string) => {
        if (action !== UsabillaEventActions.CampaignClose) {
          return;
        }

        this._handleSurveyClosure();
      },
    );
  }

  private _handleSurveyClosure(): void {
    const userData = this._userDataService.getUserData();
    const isAnalyticsUser = this._config.analyticsUserIds.includes(userData.user_id);

    const currentRaw = this._storage.getItem(this._config.cookieName);
    const currentCount = Number(currentRaw) || 0;
    const newCount = currentCount + 1;

    const days = isAnalyticsUser
      ? this._config.expirationDays.analytics
      : this._config.expirationDays.users;

    this._storage.set(
      this._config.cookieName,
      newCount.toString(),
      {
        path: '/',
        domain: this._getTopLevelDomain(),
      },
      days,
    );

    if (newCount === this._config.maxClosureCount) {
      try {
        this._usabilla.executeEvent('trigger', 'survey_seen_3x');
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'GetFeedbackService' },
        });
      }
    }
  }

  private _getTopLevelDomain(): string {
    return location.hostname.replace(/^my\./, '.');
  }
}
